import type { QueryClient } from '@tanstack/react-query';

/**
 * App-wide data freshness, driven by the two conventions this codebase
 * already follows:
 *
 *   1. Every query key starts with its API endpoint path — ['/dispatch/jobs',
 *      id], ['/my-dispatch', ...], [config.endpoint] in CrudPage.
 *   2. Every write goes through `api()` in lib/api.ts.
 *
 * So instead of each mutation hand-wiring invalidations, `api()` reports every
 * successful non-GET here. We derive the resource root from the URL, refresh
 * every query family under it in THIS tab, and broadcast the same to every
 * other tab of this browser (mirroring the existing `atlas-auth` channel).
 * Screens that render newer data update live — no refresh, no re-navigation —
 * regardless of which page, dialog, or tab performed the write.
 *
 * Cross-device propagation is separate: push notifications (see
 * features/notifications) and the pages' own polls cover that.
 */

/** Writes to one resource can change what another resource's GETs render.
 *  Keyed by resource root → additional query-key roots to refresh. */
const RELATED_ROOTS: Record<string, string[]> = {
  // Line items render inside job lists (productSummary) and driver views.
  '/dispatch-job-products': ['/dispatch/jobs', '/my-dispatch'],
  // Jobs render in the driver list/detail (pseudo-key '/my-dispatch') and
  // deleting a job orphans its line-item queries.
  '/dispatch/jobs': ['/my-dispatch', '/dispatch-job-products'],
};

/** RPC-style POSTs that mint/consume something but change no queryable
 *  resource. CRITICAL: '/widget-builder/session' is a POST used INSIDE a
 *  useQuery whose key equals its own derived root — reporting it would
 *  invalidate that key, refetch, POST again, and loop forever. */
const EXCLUDED_PATHS = [
  '/widget-builder/session',
  '/dispatch/jobs/allocate-number',
  '/auth/',
];

let client: QueryClient | null = null;

/** Call once at app bootstrap, right after the QueryClient is constructed. */
export function registerQueryClientForDataSync(qc: QueryClient): void {
  client = qc;
}

const OBJECT_ID = /^[0-9a-f]{24}$/i;

/** '/dispatch/jobs/68a.../bulk?x=1' → '/dispatch/jobs' — strip the query
 *  string, Mongo ids, and the bulk suffix so what remains matches the
 *  query-key convention. */
function resourceRoot(path: string): string {
  const segs = path.split('?')[0].split('/').filter(Boolean).filter((s) => !OBJECT_ID.test(s));
  if (segs[segs.length - 1] === 'bulk') segs.pop();
  return '/' + segs.join('/');
}

/** Refresh every query family under the given roots (plus RELATED_ROOTS
 *  fan-out) — the single source of truth for "which query keys render this
 *  resource". Also used by the push-notification path (job-cache.ts). */
export function invalidateRoots(qc: QueryClient, roots: Iterable<string>): void {
  const targets = new Set<string>();
  for (const r of roots) {
    targets.add(r);
    for (const rel of RELATED_ROOTS[r] ?? []) targets.add(rel);
  }
  void qc.invalidateQueries({
    predicate: (q) => {
      const k0 = q.queryKey[0];
      if (typeof k0 !== 'string') return false;
      for (const t of targets) {
        if (k0 === t || k0.startsWith(`${t}/`)) return true;
      }
      return false;
    },
  });
}

// Same-origin tab fan-out. BroadcastChannel never echoes to the sender, so
// local + broadcast can't loop. Naming follows the existing 'atlas-auth'
// channel in lib/api.ts.
const channel: BroadcastChannel | null =
  typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('atlas-data')
    : null;

channel?.addEventListener('message', (e: MessageEvent) => {
  const msg = e.data;
  if (client && msg && msg.type === 'writes' && Array.isArray(msg.roots)) {
    invalidateRoots(client, msg.roots as string[]);
  }
});

// Trailing debounce: one dialog Save can fan out into several sequential
// requests (job PUT + one per line item); re-arming the timer on every report
// batches the whole burst into a single invalidation + one broadcast instead
// of hammering refetches per request.
let pending = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flush(): void {
  flushTimer = null;
  const roots = pending;
  pending = new Set();
  if (client) invalidateRoots(client, roots);
  channel?.postMessage({ type: 'writes', roots: [...roots] });
}

/** Called by lib/api.ts after every successful non-GET request. */
export function reportDataWrite(path: string): void {
  if (EXCLUDED_PATHS.some((p) => path.startsWith(p))) return;
  pending.add(resourceRoot(path));
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, 250);
}
